/**
 * Sprint T-16.1 · Automation Center · Formatters (pure functions + tiny components).
 *
 * RelativeTime · ScheduleHumanReadable · shared date helpers.
 * NO business logic · presentation formatting only.
 */
import React from "react";

const RTF = typeof Intl !== "undefined" && Intl.RelativeTimeFormat
  ? new Intl.RelativeTimeFormat("es-ES", { numeric: "auto" })
  : null;

const UNITS = [
  ["year", 31536000],
  ["month", 2592000],
  ["week", 604800],
  ["day", 86400],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

function _relative(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const diff = (d.getTime() - Date.now()) / 1000;
    for (const [unit, sec] of UNITS) {
      if (Math.abs(diff) >= sec || unit === "second") {
        const value = Math.round(diff / sec);
        return RTF ? RTF.format(value, unit) : `${value} ${unit}`;
      }
    }
  } catch {
    return iso;
  }
  return iso;
}

export function RelativeTime({ iso, testId, className }) {
  if (!iso) {
    return (
      <span data-testid={testId} className={className || "text-[10px] text-[var(--ag-text-dim)]"}>
        —
      </span>
    );
  }
  return (
    <span
      data-testid={testId}
      className={className || "text-[10px] text-[var(--ag-text-dim)]"}
      title={iso}
    >
      {_relative(iso)}
    </span>
  );
}

export function AbsoluteTime({ iso, testId, className, format = "short" }) {
  if (!iso) return <span data-testid={testId} className={className}>—</span>;
  let out = iso;
  try {
    const d = new Date(iso);
    out = d.toLocaleString("es-ES", {
      dateStyle: format === "long" ? "medium" : "short",
      timeStyle: "medium",
    });
  } catch {
    /* ignore */
  }
  return (
    <span data-testid={testId} className={className || "font-mono text-[10px] text-[var(--ag-text-dim)]"} title={iso}>
      {out}
    </span>
  );
}

/**
 * ScheduleHumanReadable · pure function that turns a ScheduleSpec into
 * a Spanish-readable string. Does NOT compute next_run_at.
 */
const WEEKDAY_LABEL = {
  mon: "lunes", tue: "martes", wed: "miércoles", thu: "jueves",
  fri: "viernes", sat: "sábado", sun: "domingo",
};

export function humanReadableSchedule(schedule) {
  if (!schedule) return "sin schedule";
  switch (schedule.kind) {
    case "manual":
      return "Manual (bajo demanda)";
    case "every": {
      const s = Number(schedule.every_seconds || 0);
      if (s < 60) return `cada ${s} s`;
      if (s < 3600) return `cada ${Math.round(s / 60)} min`;
      return `cada ${Math.round(s / 3600)} h`;
    }
    case "daily":
      return `diario a las ${schedule.at || "??"} (${schedule.tz || "UTC"})`;
    case "weekly":
      return `semanal · ${WEEKDAY_LABEL[schedule.day] || schedule.day} · ${schedule.at || "??"} (${schedule.tz || "UTC"})`;
    case "monthly":
      return `mensual · día ${schedule.day_of_month} · ${schedule.at || "??"} (${schedule.tz || "UTC"})`;
    default:
      return schedule.kind || "sin schedule";
  }
}

export function ScheduleHumanReadable({ schedule, testId }) {
  return (
    <span data-testid={testId} className="text-xs text-[var(--ag-text)]">
      {humanReadableSchedule(schedule)}
    </span>
  );
}
